import {
  FeatureFlag,
  getPublicEnvVars,
  DEFAULT_BOT_FILTER_CONFIG,
  type BotFilterConfig,
} from '@b-stock/fe-core/bstock-next/helpers'

export type TabComponentEnabled =
  | {
      aboutEnabledStorefrontIds: string[]
      faqEnabledStorefrontIds: string[]
      conditionsEnabledStorefrontIds: string[]
      shippingEnabledStorefrontIds: string[]
      deviceUnlockingTermsEnabledStorefrontIds: string[]
    }
  | false

type UniversalSearchEnabled = {
  enabled: boolean
  excludedStorefrontSlugs?: string[] // For Storefront pages
  excludedStorefrontIds?: string[] // For Listing/Order Details pages
}

export type DealsRangesConfig = {
  enabled: boolean
  rangeOne?: {
    max?: number
    min?: number
    name: string
  }
  rangeTwo?: {
    max?: number
    min?: number
    name: string
  }
  rangeThree?: {
    max?: number
    min?: number
    name: string
  }
}

export type FmvDealBadgeConfig = {
  enabled: boolean
  excludedStorefrontIds?: string[]
  excludedStorefrontSlugs?: string[]
}

export type LowBidderDealBadgeConfig = {
  enabled: boolean
  /**
   * Keyed by the exact search-result category string. A category is a priority
   * category only if it has a threshold here — there is no separate category
   * list. Absent, empty, or non-numeric entries yield no badge.
   */
  bidderThresholdsByCategory?: Record<string, number>
  /**
   * ID-only — the ALP search-API listing type has no `storefrontSlug`.
   * Gates eligibility, so it applies in both A/B arms: an environment's On and
   * Off variations must carry the same list. Absent, nothing is eligible; an
   * explicit `[]` means "exclude nobody".
   */
  excludedStorefrontIds?: string[]
}

type ProblemFreeOrderRateConfig = {
  enabled: boolean
  variant?: 'a' | 'b' | 'c'
  excludedStorefrontIds?: string[]
  excludedStorefrontSlugs?: string[]
}

export type FlagValues<Flag> = Flag extends FeatureFlag<infer V> ? V : never

export type ManifestSummarizeLabelVariant =
  | 'summarize'
  | 'ai-summary'
  | 'quick-view'

export const FeatureFlags = {
  ENABLE_ABOUT_FAQ_BY_STOREFRONT_ID: new FeatureFlag<
    TabComponentEnabled | false
  >('enable-about-faq-by-storefront-id', {
    aboutEnabledStorefrontIds: [],
    faqEnabledStorefrontIds: [],
    conditionsEnabledStorefrontIds: [],
    shippingEnabledStorefrontIds: [],
    deviceUnlockingTermsEnabledStorefrontIds: [],
  }),
  /** Emergency kill switch: stop home-portal from reading seller-profiles from Payload. */
  ENABLE_CMS_SELLER_PROFILE: new FeatureFlag(
    'enable-cms-seller-profile',
    false
  ),
  /**
   * When true, home-portal root layout title, description, Open Graph, and
   * Organization / WebPage / WebSite JSON-LD read from the cms-portal
   * `global-seo` global. The homepage (`/`) document title, meta description, and
   * social cards use the Global → “HomePage SEO” fields (falls back
   * to the previous static copy when the flag is off or the global fetch
   * fails). When false, use hardcoded defaults (no CMS fetch for SEO).
   */
  ENABLE_CMS_GLOBAL_SEO: new FeatureFlag('enable-cms-global-seo', false),
  ENABLE_OFFERS_PAGE_FOR_BUYERS: new FeatureFlag(
    'enable-offers-page-for-buyers',
    false
  ),
  ENABLE_DEALS_AND_RECOMMENDATIONS: new FeatureFlag(
    'enable-deals-and-recommendations',
    false
  ),
  ENABLE_SEAMLESS_BIDDING: new FeatureFlag('enable-seamless-bidding', false),
  ENABLE_BRAZE_MARKETING_POPUPS: new FeatureFlag(
    'enable-braze-marketing-popups',
    false
  ),

  CONFIGURE_HOME_PAGE_CAROUSEL: new FeatureFlag<string[]>(
    'configure-home-page-carousel',
    ['bargain', 'target', 'spotlight', 'amazon', 'footlocker']
  ),

  ENABLE_HOME_PAGE_FOOTLOCKER_SPOTLIGHT_CARD: new FeatureFlag(
    'enable-footlocker-spotlight-carousel-card',
    false
  ),

  ENABLE_HOME_PAGE_TARGET_STOREFRONT_ID_UPDATE: new FeatureFlag(
    'enable-target-storefront-id-update',
    false
  ),

  ENABLE_HOME_PAGE_WALMART_RAIL: new FeatureFlag(
    'enable-home-page-walmart-rail',
    false
  ),

  ENABLE_ALL_LISTINGS_SUBCATEGORY_FILTER: new FeatureFlag(
    'enable-all-listings-subcategory-filter',
    false
  ),
  ENABLE_SELLER_STOREFRONT_SUBCATEGORY_FILTER: new FeatureFlag(
    'enable-seller-storefront-subcategory-filter',
    false
  ),
  /**
   * Where the marketplace header and footer get their links from — the CMS when on, the values
   * bundled in fe-core when off.
   *
   * Separate from `ENABLE_CMS_MARKETPLACE_CONTENT` by blast radius rather than by ticket: these
   * two render on every page, so a bad fetch here is sitewide.
   */
  ENABLE_CMS_MARKETPLACE_NAV: new FeatureFlag(
    'enable-cms-marketplace-nav',
    false
  ),
  /**
   * Where the homepage and the storefront tabs get their content from — the CMS when on, the
   * copy hardcoded in this repo when off. One flag rather than one per section: each section
   * falls back independently when its CMS group is empty, so copy can go live a section at a
   * time under a single switch, and a rollback is one toggle rather than six.
   *
   * Does not cover the marketing pages or blog posts, which have always been CMS-sourced.
   */
  ENABLE_CMS_MARKETPLACE_CONTENT: new FeatureFlag(
    'enable-cms-marketplace-content',
    false
  ),
  ENABLE_PAY_WITH_TERMS: new FeatureFlag('enable-pay-with-terms', false),

  DATADOG_SESSION_RECORDING_SAMPLE_RATE: new FeatureFlag(
    'datadog-session-recording-sample-rate',
    0
  ),
  // Minutes a tab stays hidden before idle Pusher sockets close; 0 is off.
  PUSHER_HIDDEN_DISCONNECT_MINUTES: new FeatureFlag(
    'pusher-hidden-disconnect-minutes',
    0
  ),
  ENABLE_COSTCO_LAUNCH_BANNER: new FeatureFlag(
    'enable-costco-launch-banner',
    false
  ),
  ENABLE_NET_TERMS_BANNER: new FeatureFlag('enable-net-terms-banner', false),
  ENABLE_SHOP_MOBILE_BANNER: new FeatureFlag(
    'enable-shop-mobile-banner',
    false
  ),

  ENABLE_AMAZON_PROMO_BANNER: new FeatureFlag(
    'enable-amazon-promo-banner',
    false
  ),

  /**
   * Feature flag for homepage welcome banner A/B test (BP-857).
   * Variant 'control': Original messaging focused on marketplace size
   * Variant 'treatment': Concise messaging with "Source better. Scale faster."
   * TODO: Remove this feature flag once A/B test is complete and winner implemented
   */
  HOMEPAGE_WELCOME_BANNER_TEST: new FeatureFlag<'control' | 'treatment'>(
    'homepage-welcome-banner-test',
    'control'
  ),

  ENABLE_SPLIT_PAYMENT: new FeatureFlag('enable-split-payment', false),

  /**
   * Credit card convenience fee rate, expressed as a percent (e.g. `3` for 3%,
   * `3.5` for 3.5%). Read at the call sites of `calculateCreditCardConvenienceFee`
   * and converted to a multiplier with `× 0.01`. The in-code fallback (`3.5`)
   * preserves the historical 3.5% rate when LD is unreachable or the flag is
   * missing.
   */
  CREDIT_CARD_CONVENIENCE_FEE_PERCENT: new FeatureFlag(
    'credit-card-convenience-fee-percent',
    3.5
  ),

  IMAGE_OPTIMIZATION_STRATEGY: new FeatureFlag<
    'next-image-optimization' | 'none' | 'third-party'
  >('image-optimization-strategy', 'next-image-optimization'),

  ENABLE_BETA_AUCTION_LIST_VIEW: new FeatureFlag(
    'enable-beta-auction-list-view',
    getPublicEnvVars().PUBLIC_APP_ENV === 'dev'
  ),

  ENABLE_UNIVERSAL_SEARCH_BAR_TOP_NAV: new FeatureFlag<UniversalSearchEnabled>(
    'enable-universal-search-bar-top-nav',
    { enabled: false } satisfies UniversalSearchEnabled
  ),

  /**
   * Feature flag for home page saved search module A/B/C test.
   * Variant 'variation a': No saved searches block
   * Variant 'variation b': Saved searches enabled, counts disabled
   * Variant 'variation c': Saved searches + counts enabled (full experience)
   * TODO: Remove this feature flag once A/B/C test is complete (BP-549)
   */
  HOME_PAGE_SAVED_SEARCH_ABC: new FeatureFlag<
    'variation a' | 'variation b' | 'variation c'
  >('home-page-saved-search-abc', 'variation a'),

  /**
   * Feature flag for home page recent searches module.
   * When enabled, shows the buyer's last 10 recent searches on the homepage.
   * TODO: Remove this feature flag once live (BP-485)
   */
  ENABLE_HOMEPAGE_RECENT_SEARCH: new FeatureFlag(
    'enable-homepage-recent-search',
    false
  ),

  BID_BUTTON_COLOR: new FeatureFlag<'blue' | 'orange' | 'red'>(
    'bid-button-color',
    'blue'
  ),

  /**
   * Feature flag for search autocomplete suggestions.
   * TODO: Ticket to remove this feature flag (BP-394)
   */
  ENABLE_SEARCH_AUTO_COMPLETE_BOX: new FeatureFlag(
    'enable-search-auto-complete-box',
    false
  ),

  /**
   * Feature flag for search auto-suggestion box (recent searches + trending searches).
   * Shows suggestions on focus instead of typing. Alternative to autocomplete box.
   */
  ENABLE_SEARCH_AUTO_SUGGESTION_BOX: new FeatureFlag(
    'enable-search-auto-suggestion-box',
    false
  ),

  SEARCH_BAR_ANIMATED_PLACEHOLDER_CONFIG: new FeatureFlag<{
    activationRate: number
    placeholders?: []
  }>('search-bar-animated-placeholder-config', { activationRate: 0 }),

  /**
   * Feature flag for watchlist carousel on homepage.
   * Shows user's watchlisted items in a carousel at the top of the homepage.
   * TODO: Remove this feature flag once fully rolled out (BP-556)
   */
  ENABLE_ACCOUNT_PAGES_BEST_DEALS_CAROUSEL: new FeatureFlag(
    'enable-account-pages-best-deals-carousel',
    false
  ),

  /**
   * Browse nav bar on the buyer account list surfaces. Release kill switch.
   */
  ENABLE_ACCOUNT_PAGES_BROWSE_NAV: new FeatureFlag(
    'enable-account-pages-browse-nav',
    false
  ),

  /**
   * Feature flag for watchlist carousel on homepage.
   * Shows user's watchlisted items in a carousel at the top of the homepage.
   * TODO: Remove this feature flag once fully rolled out (BP-556)
   */
  ENABLE_WATCHLIST_CAROUSEL: new FeatureFlag(
    'enable-watchlist-carousel',
    false
  ),

  /**
   * Feature flag for new seller carousel on homepage.
   * Shows listings from storefronts flagged as newSeller to drive discovery.
   */
  ENABLE_NEW_SELLER_CAROUSEL: new FeatureFlag(
    'enable-new-seller-carousel',
    false
  ),

  /**
   * Experiment flag for new buyer listings feature (GLOB-3525).
   * Gates the new buyer carousel on the home page and the Starter Lots filter
   * on the All Listings page.
   */
  ENABLE_NEW_BUYER_LISTINGS: new FeatureFlag(
    'enable-new-buyer-listings',
    false
  ),

  /**
   * Feature flag for all auto-pay features.
   * When enabled, shows new payment method display with shipping/payment info,
   * wire transfer option, and inline payment method selection.
   * When disabled, uses original bid confirmation layout.
   */
  AUTOPAY_ENFORCED: new FeatureFlag('autopay-enforced', false),
  ENABLE_CONTRACT_AUTOPAY: new FeatureFlag('enable-contract-autopay', false),

  /**
   * Feature flag for parcel shipping rate options in the bid/buy-now flow (FP-1904).
   * When enabled, Binding + Parcel listings render the full set of parcel
   * rates as a read-only selectable card list (server preselects the cheapest),
   * suppressing the carrier-change dropdown. Display only — selection interactivity
   * is FP-1910. Default off; the BE reads the same LD key.
   */
  ENABLE_PARCEL_QUOTES: new FeatureFlag('enable-parcel-quotes', false),

  /**
   * Seller account IDs whose Binding + Parcel listings show parcel rates while
   * ENABLE_PARCEL_QUOTES is off. The BE reads the same LD key.
   */
  PARCEL_QUOTES_EXCEPTION_LIST: new FeatureFlag<string[]>(
    'parcel-quotes-exception-list',
    []
  ),

  /**
   * Feature flag for payment method selection flow in AddPaymentMethodModal.
   * When enabled, shows initial selection step (ACH vs Credit Card).
   * When disabled, uses original behavior (directly shows AddPaymentMethodForm).
   */
  ENABLE_BALANCE_CREDIT_CARDS: new FeatureFlag(
    'enable-balance-credit-cards',
    false
  ),

  /**
   * Configuration for bot filtering in Segment analytics.
   * Controls which user agents are blocked from tracking to reduce MTU costs.
   * Set enabled: false to disable all bot filtering.
   * Do Not Delete This Flag
   */
  CONFIGURE_TELEMETRY_BOT_FILTER: new FeatureFlag<BotFilterConfig>(
    'configure-telemetry-bot-filter',
    DEFAULT_BOT_FILTER_CONFIG
  ),

  /**
   * Feature flag for deals filter on all auctions and seller storefront pages.
   * Shows filter with options: Fair Price, Good Price, Great Price.
   * TODO: Remove this feature flag once fully rolled out (BP-853)
   */
  ENABLE_DEALS_FILTER: new FeatureFlag('enable-alp-deals-filter', false),

  /**
   * Feature flag for the ALP List View Deal Graph (GLOB-5143) — a bubble plot
   * of deal quality against time-to-close, swapped in for the results table.
   */
  ENABLE_ALP_DEAL_GRAPH: new FeatureFlag('enable-alp-deal-graph', false),

  /**
   * Master kill switch for the "Download All Manifests" export (the
   * LaunchDarkly key predates realizing it needed to cover more than ALP —
   * see ListingSearchView.tsx for how it's actually used). Off reverts
   * Storefront to its old page-only export too, not just ALP.
   */
  ENABLE_ALP_MANIFEST_EXPORT: new FeatureFlag(
    'enable-alp-manifest-export',
    false
  ),

  /**
   * Feature flag for deals ranges configuration.
   * Configuration defines the min/max thresholds and display names for each deal range.
   * Default values provide fallback labels if LaunchDarkly fails.
   */
  ENABLE_DEALS_RANGES: new FeatureFlag<DealsRangesConfig>(
    'enable-deals-ranges',
    {
      enabled: false,
      rangeOne: {
        max: 0.1,
        min: -0.1,
        name: 'Fair Price',
      },
      rangeTwo: {
        max: -0.1,
        min: -0.2,
        name: 'Good Price',
      },
      rangeThree: {
        max: -0.2,
        name: 'Great Price',
      },
    }
  ),

  /**
   * Feature flag for Best Deals Tracker on homepage.
   * Shows a table of inventory performance against Typical B-Stock Price.
   */
  ENABLE_BEST_DEALS_TRACKER_HOME_PAGE: new FeatureFlag(
    'enable-best-deals-tracker-home-page',
    false
  ),

  /**
   * Feature flag for typical selling price calculation in bid box.
   * Shows estimated typical selling price above max bid buttons.
   * TODO: Remove this feature flag and mock data once backend provides real data (BP-810)
   */
  ENABLE_BID_BOX_SELLING_PRICE_CALCULATION: new FeatureFlag(
    'enable-bid-box-selling-price-calculation',
    false
  ),

  /**
   * Feature flag for typical selling price calculation on Costco listings.
   * When main flag is enabled, this controls whether Costco listings show the calculation.
   * TODO: Remove this feature flag once fully rolled out
   */
  ENABLE_BID_BOX_SELLING_PRICE_CALCULATION_COSTCO: new FeatureFlag(
    'enable-bid-box-selling-price-calculation-costco',
    false
  ),

  /**
   * FF for the Enhanced Media Gallery on the LDP (GLOB-3902 / GLOB-3903).
   * When enabled, swaps out old MediaSection for new ListingMediaCard.
   */
  ENABLE_HP_LDP_ENHANCED_MEDIA_GALLERY: new FeatureFlag(
    'enable-hp-ldp-enhanced-media-gallery',
    false
  ),

  /**
   * Feature flag for video thumbnails in the listing details media gallery.
   * When enabled, parses YouTube/Vimeo links from listing descriptions and displays them as video thumbnails
   * TODO: Remove this feature flag once fully rolled out (GLOB-3524)
   */
  ENABLE_VIDEO_IN_MEDIA_GALLERY: new FeatureFlag(
    'enable-video-in-media-gallery',
    false
  ),

  /**
   * FF for Phase 2 features of the Enhanced Media Gallery (BP-1867).
   * Requires ENABLE_HP_LDP_ENHANCED_MEDIA_GALLERY to also be on.
   * When enabled, adds: photo count badge, grid view toggle, bulk photo download.
   */
  ENABLE_HP_LDP_MEDIA_GRID_VIEW: new FeatureFlag(
    'enable-ldp-media-grid-view',
    false
  ),

  /**
   * Feature flag for FMV deal badge on listing details page.
   * Shows a single "Hot Deal" badge above the bid box for top-tier (Great Price) FMV listings only.
   * TODO: Remove this feature flag once A/B test is complete AND feature is permanently enabled for all storefronts (GLOB-3682)
   */
  ENABLE_FMV_DEAL_BADGE: new FeatureFlag<FmvDealBadgeConfig>(
    'enable-deal-badge-fmv',
    {
      enabled: false,
    }
  ),

  /**
   * Feature flag for FMV deal badge on All Listings Page cards.
   * Shows a single "Hot Deal" badge in the top-right corner for top-tier (Great Price) FMV listings only.
   * Independent of LDP badge for separate A/B testing.
   */
  ENABLE_FMV_DEAL_BADGE_ALP: new FeatureFlag<FmvDealBadgeConfig>(
    'enable-alp-fmv-badge',
    {
      enabled: false,
    }
  ),

  /**
   * Feature flag for the low-bidder deal badge on All Listings Page grid cards
   * (GLOB-5164). Shows the existing "Hot Deal" badge on listings that are in a
   * priority category, are a top-tier ("Great Price") deal, and have fewer
   * bidders than that category's configured threshold.
   *
   * Thresholds are config-only and must never be defaulted in code.
   */
  ENABLE_LOW_BIDDER_DEAL_BADGE_ALP: new FeatureFlag<LowBidderDealBadgeConfig>(
    'enable-low-bidder-deal-badge-alp',
    {
      enabled: false,
    }
  ),

  /**
   * Feature flag for MSRP percentage filter on listing pages.
   * When enabled, shows the percentage of MSRP filter.
   * TODO: Remove this feature flag once A/B test is complete
   */
  SHOW_MSRP_PERCENTAGE_FILTER: new FeatureFlag(
    'show-msrp-percentage-filter',
    false
  ),

  /**
   * Entitlement flag identifying the current user as a Premier Buyer
   * (formerly "Power Buyer") (GLOB-4597).
   * Targeted in LaunchDarkly to specific buyer accounts with elevated access.
   */
  PREMIER_BUYER: new FeatureFlag('premier-buyer', false),

  /**
   * Gates buyer-facing First Dibs UI: the LDP "opens to everyone"
   * countdown banner, the First Dibs strip on the bid card, and early-access
   * bidding / Buy Now at the group's early price. Default off.
   */
  ENABLE_FIRST_DIBS: new FeatureFlag('enable-first-dibs', false),

  /**
   * Orders/Bids homepage dashboard; gated behind PREMIER_BUYER in
   * `HomeDashboardBand`. Also the experiment arm assignment (sticky 50/50
   * fallthrough in LaunchDarkly).
   */
  HOME_ORDERS_BIDS_DASHBOARD: new FeatureFlag(
    'home-orders-bids-dashboard',
    false
  ),

  /**
   * Feature flag for Landed Cost as % of MSRP filter on listing pages.
   * Visible only when this flag is on AND useIsPremierBuyer() returns true.
   */
  SHOW_LANDED_COST_PERCENTAGE_FILTER: new FeatureFlag(
    'show-landed-cost-percentage-filter',
    false
  ),

  /**
   * Feature flag for "Compare These Listings (Limit 4)" on the All Auctions
   * page (GLOB-3881). When enabled, buyers can select up to 4 listings to
   * compare side by side.
   * Read before the sign-in check, so its LD targeting must keep the
   * anonymous → false rule.
   */
  ENABLE_ALP_COMPARE_LISTINGS: new FeatureFlag(
    'enable-alp-compare-listings',
    false
  ),

  /**
   * Feature flag for logged-in homepage personalized welcome banner A/B test (GLOB-3646).
   * Variant 'control': Existing rotating marketing carousel
   * Variant 'treatment': Personalized static welcome banner with user's full name
   * Primary metric: Bids Placed Count
   * TODO: Remove this feature flag once A/B test is complete
   */
  LOGGED_IN_HOMEPAGE_WELCOME_BANNER: new FeatureFlag<'control' | 'treatment'>(
    'logged-in-homepage-welcome-banner',
    'control'
  ),

  /**
   * Feature flag for FMV deal badge on Seller Storefront listing cards.
   * When enabled, shows a single "Hot Deal" badge for top-tier (Great Price)
   * storefront listings, unless the storefront is excluded.
   */
  ENABLE_SELLER_STOREFRONT_DEALS_BADGE: new FeatureFlag<FmvDealBadgeConfig>(
    'enable-seller-storefront-deals-badge',
    { enabled: false }
  ),

  /**
   * Feature flag for deals filter on Seller Storefront pages.
   * When enabled, shows the deals filter for storefronts unless the storefront is excluded.
   */
  ENABLE_SELLER_STOREFRONT_DEALS_FILTER: new FeatureFlag<FmvDealBadgeConfig>(
    'enable-seller-storefront-deals-filter',
    { enabled: false }
  ),

  /**
   * Feature flag for the Average Loss Margin module on the homepage dashboard.
   * When enabled, shows the user their average percentage below the winning bid
   * based on their last 20 losses.
   */
  ENABLE_AVERAGE_LOSS_MARGIN_MODULE: new FeatureFlag<{ enabled: boolean }>(
    'enable-average-loss-margin-module',
    { enabled: false }
  ),

  ENABLE_WIN_OPPORTUNITIES_MODULE: new FeatureFlag<{ enabled: boolean }>(
    'enable-win-opportunities-module',
    { enabled: false }
  ),

  /**
   * Feature flag for the Problem-Free Order Rate filter on the All Listings Page.
   * Auth-gated (logged-in users only), ALP-only.
   * The `variant` field drives an A/B/C test on the filter label:
   *   'a' → "Facility Performance Rate"
   *   'b' → "Facility Score"
   *   'c' → "Problem-Free Order Rate"
   */
  ENABLE_ALP_PROBLEM_FREE_ORDER_RATE:
    new FeatureFlag<ProblemFreeOrderRateConfig>(
      'enable-alp-problem-free-order-rate',
      { enabled: false, variant: 'c' }
    ),

  /**
   * Feature flag for the Problem-Free Order Rate display on the Listing Details Page.
   * Auth-gated (logged-in users only). Uses mock data until the real API is available.
   * The `variant` field drives an A/B/C test on the label:
   *   'a' → "Facility Performance Rate"
   *   'b' → "Facility Score"
   *   'c' → "Problem-Free Order Rate"
   */
  ENABLE_LDP_PROBLEM_FREE_ORDER_RATE:
    new FeatureFlag<ProblemFreeOrderRateConfig>(
      'enable-ldp-problem-free-order-rate',
      { enabled: false, variant: 'c' }
    ),

  ENABLE_PFOR_STOREFRONT_EXCLUSION: new FeatureFlag<boolean>(
    'enable-pfor-storefront-exclusion',
    false
  ),

  /**
   * Feature flag for the Paid-to-Shipped time metric on the Listing Details Page.
   * Auth-gated (logged-in users only), excluded from Costco listings.
   * 'off': Not shown
   * 'text': Shows as a label/value row inside the Shipping & Costs grey box
   * 'banner': Shows as a blue info banner inside the Shipping & Costs grey box
   * Data sourced from GET /v1/facility-metrics/:locationOriginalId via facilityMetricsQuery.
   */
  ENABLE_LDP_P2S_METRICS_AB: new FeatureFlag<'off' | 'text' | 'banner'>(
    'enable-ldp-p2s-metrics-ab',
    'off'
  ),

  /**
   * Feature flag for CMS-driven blog routes (`/seller/blog`, `/buyer/blog`,
   * category drill-downs, and `/{audience}/blog/[slug]`). When disabled, all
   * blog routes return 404. (BP-1381 / GLOB-4664)
   */
  ENABLE_BLOG: new FeatureFlag('enable-blog-posts', false),

  /**
   * Feature flag for CMS-driven marketing page routes (top-level `/[slug]`).
   * When disabled, all CMS page routes return 404. (BP-1381)
   */
  ENABLE_CMS_PAGES: new FeatureFlag('enable-cms-pages', false),

  /**
   * Feature flag for the Seller Condition/Grade Enhancement on the Listing Details Page.
   * For whitelisted storefronts, replaces the B-Stock Condition field with the Seller Grade
   * when Seller Grade data is present. Falls back to showing Condition when no Seller Grade exists.
   */
  ENABLE_LDP_SELLER_CONDITION_GRADE_ENHANCEMENT: new FeatureFlag<
    | {
        enabled: boolean
        includedStorefrontIds?: string[]
        includedStorefrontSlugs?: string[]
      }
    | false
  >('enable-ldp-seller-condition-grade-enhancement', false),

  /**
   * Feature flag for the Seller Grade filter on Seller Storefront pages.
   * For whitelisted storefronts, replaces the Condition filter with a Seller Grade filter.
   */
  ENABLE_STOREFRONT_SELLER_GRADE_FILTER: new FeatureFlag<
    | {
        enabled: boolean
        includedStorefrontIds?: string[]
        includedStorefrontSlugs?: string[]
      }
    | false
  >('enable-storefront-seller-grade', false),

  /*
   * Feature flag for Saved Search UX improvements (GLOB-3795).
   * TODO: Remove this feature flag once improvements are fully rolled out (BP-1883)
   */
  ENABLE_SAVED_SEARCH_UX_IMPROVEMENTS: new FeatureFlag(
    'enable-saved-search-ux-improvements',
    false
  ),
  /*
   * Feature flag for normalizing Memory and Capacity displays/filters.
   * When enabled:
   * - Memory filter appears in the search sidebar alongside Capacity
   * - Memory column appears in mobile AG Grid layouts alongside Capacity
   * - Memory rollup section appears on Listing Details Page alongside Capacity
   *   (LDP memory data requires backend support for lot.memories on LotDto)
   */
  ENABLE_MEMORY_ROLLUP_VISIBILITY: new FeatureFlag(
    'enable-memory-rollup-visibility',
    false
  ),

  /**
   * Feature flag for the live chat widget A/B test on key pages (GLOB-4031 / FP-1270).
   * true (control): chat widget loads on Homepage, ALP, and LDP.
   * false (test): chat widget is suppressed on those pages.
   * The live chat GTM tag is blocked from its normal triggers on those
   * pages and fires only when the app pushes `enable_chat_widget` via
   * LiveChatWidgetGate, which requires this flag AND functionality_storage consent.
   * TODO: Remove this feature flag once A/B test is complete.
   */
  ENABLE_LIVE_CHAT_KEY_PAGES: new FeatureFlag(
    'enable-live-chat-key-pages',
    true
  ),

  //TODO: Remove this feature flag once fully rolled out
  NEW_SELLER_ALP_LABEL: new FeatureFlag<'control' | 'treatment'>(
    'new-seller-alp-label',
    'control'
  ),

  ENABLE_BULK_ACTIONS: new FeatureFlag('enable-bid-bulk-actions', false),

  /**
   * Gates the "Add to Watchlist" option in the Bulk Actions dropdown
   * (ALP/Storefront) and the "Add to Watchlist" button on My Bids.
   * `ENABLE_BULK_ACTIONS` controls the bulk-selection checkboxes beside
   * listing cards; this flag controls the actual bulk-watchlist
   * implementation on top of that selection, so the two can be toggled
   * independently.
   */
  ENABLE_BULK_WATCHLIST_ADD: new FeatureFlag(
    'enable-bulk-watchlist-add',
    false
  ),

  /**
   * Feature flag for the "Find Similar Listings" button on My Bids BidCard (BP-1879).
   * When enabled, shows the button on bid cards where the listing still exists and
   * the lot has a usable top category.
   * TODO: Remove this feature flag once fully rolled out.
   */
  ENABLE_MY_BIDS_SIMILAR_LISTINGS: new FeatureFlag(
    'enable-my-bids-similar-listings',
    false
  ),

  /**
   * "Better Odds": on My Bids losing/lost rows, offers open same-category
   * listings below their unique-bidder magic number; on the homepage,
   * swaps the "Recommended for You" carousel slot to the same gap-targeted
   * listings when the buyer has losing bids. Gates both surfaces with a
   * single flag so the A/B reads as one buyer-level split. Requires
   * ENABLE_MY_BIDS_INLINE_BID_BOX for the My Bids surface — the LOSING/LOST
   * banner it anchors to only renders under that flag.
   */
  ENABLE_BETTER_ODDS: new FeatureFlag('enable-better-odds', false),

  /**
   * Kill switch for Home Page carousels (emergency relief valve to alleviate
   * home-portal load). Does not apply to the logged-out redesign
   * (ENABLE_LOGGED_OUT_HOMEPAGE_REDESIGN): its closing-soon rail is only shed
   * by turning that flag off.
   */
  KILL_SWITCH_DISABLE_HOME_CAROUSELS: new FeatureFlag<
    null | 'all' | 'unauthenticated' | 'authenticated'
  >('kill-switch-disable-home-carousels', null),

  /**
   * Offers buyers a Buy Now option alongside bidding during a concurrent
   * (CBIN) phase. When disabled, a concurrent phase is presented as auction
   * only.
   */
  ENABLE_CONCURRENT_BUY_NOW: new FeatureFlag(
    'enable-concurrent-buy-now',
    false
  ),

  /** [GLOB-3902] LDP Phase 1 Redesign */
  ENABLE_LDP_REDESIGN: new FeatureFlag('enable-ldp-redesign', false),

  /**
   * A/B experiment on the redesigned LDP's media gallery: 'v2' (control) is
   * the media card, 'v1' is the legacy gallery. Read only by the V2 layout, so
   * only buyers who see the redesign are enrolled.
   * TODO: Remove once the experiment is complete and the winner is permanent.
   */
  LDP_V2_MEDIA_GALLERY_AB: new FeatureFlag<'v1' | 'v2'>(
    'ldp-v2-media-gallery-ab',
    'v2'
  ),

  /**
   * [BP-2376] Enable infinite-scroll (no pagination) on the inline AG Grid
   * manifest table, replacing page-based pagination. Mirrors the fullscreen
   * modal's unconditional infinite-scroll behavior, and only applies to
   * logged-in users (the logged-out preview always stays content-sized).
   * TODO: Remove this flag once fully rolled out.
   */
  ENABLE_HP_MANIFEST_INFINITE_SCROLL: new FeatureFlag(
    'enable-hp-manifest-infinite-scroll',
    false
  ),

  /**
   * Feature flag for listing-level notes A/B test (GLOB-4026).
   * Control (A): flag off — no notes UI visible anywhere.
   * Test (B): flag on — full notes experience enabled.
   * Primary metric: Bid Occurrence. Secondary: Bid Count, Time to Bid.
   * TODO: Remove once A/B test is complete.
   */
  ENABLE_LISTING_NOTES: new FeatureFlag('enable-listing-notes', false),

  /**
   * A/B flag for GLOB-4878: un-gate Notes on LDP and auto-add to Watchlist on note save.
   * Control = false (existing gated behavior). Test B = true (un-gated).
   * Both this flag AND ENABLE_LISTING_NOTES must be ON for the new behavior to activate.
   * TODO: Remove once A/B test is complete.
   */
  NOTES_AUTO_WATCHLIST: new FeatureFlag('notes-auto-watchlist', false),

  /**
   * Rollback flag for MRM-362 (listing notes outlive the Watchlist). Gates
   * Watchlist removal behaviour (keepNote, the confirmation modals, the
   * note-aware Undo toast copy) and the LDP and listing-card note display for
   * un-watched listings (T006). Off = today's behaviour. My Bids and Watchlist
   * card note display are not gated by it.
   */
  NOTES_OUTLIVE_WATCHLIST: new FeatureFlag('notes-outlive-watchlist', false),

  /**
   * Feature flag for similar-inventory section below paginated results on ALP
   * and Storefront pages (GLOB-3460). When enabled and the buyer
   * is on the last page (or empty result set) with active non-keyword filters,
   * fetches and displays relaxed-filter listings via
   * POST /all-listings/listings/similar-search.
   * TODO: Remove once A/B test is complete.
   */
  ENABLE_SIMILAR_INVENTORY_TO_SEARCH: new FeatureFlag(
    'enable-similar-inventory-to-search',
    false
  ),

  /**
   * Feature flag for the similar-inventory shelf beneath the zero-results empty
   * state. Separate from ENABLE_SIMILAR_INVENTORY_TO_SEARCH so the two
   * placements measure independently.
   * TODO: Remove once A/B test is complete.
   */
  ENABLE_PROMOTED_LISTINGS_NO_RESULTS: new FeatureFlag(
    'enable-promoted-listings-no-results',
    false
  ),

  /**
   * Feature flag for the inline bid box on the My Bids page (BP-1888).
   * When enabled, LOSING and WINNING bid cards render the bid form inline
   * so buyers can place or raise a bid without navigating to the listing page.
   * TODO: Remove this flag once fully rolled out.
   */
  ENABLE_MY_BIDS_INLINE_BID_BOX: new FeatureFlag(
    'enable-my-bids-inline-bid-box',
    false
  ),

  /** Bulk bid checkboxes on My Bids for WINNING/LOSING cards. */
  ENABLE_MY_BIDS_BULK_BID: new FeatureFlag('enable-my-bids-bulk-bid', false),

  /**
   * [GLOB-4610] "My Bids" top-of-page filter toggle on the All Listings page
   * (List & Grid view), restricting results to live listings the buyer has
   * an active bid on.
   */
  ENABLE_MY_BIDS_LISTINGS_FILTER: new FeatureFlag(
    'enable-my-bids-listings-filter',
    false
  ),

  /**
   * [GLOB-4406] My Bids page dashboard (exposure, bid totals, winning/losing modules).
   */
  BUYER_EXPOSURE_DASHBOARD: new FeatureFlag('buyer-exposure-dashboard', false),

  /**
   * Feature flag for the AI-powered Summarize button on ALP listing cards (BP-1666).
   * Controls both whether the feature is on and which button label variant to show.
   * 'off' = feature disabled. Other values enable the feature with that label variant.
   * TODO: Remove once fully rolled out.
   */
  ENABLE_ALP_MANIFEST_SUMMARIZE: new FeatureFlag<
    'off' | 'summarize' | 'ai-summary' | 'quick-view'
  >('enable-alp-manifest-summarize', 'off'),

  /**
   * Feature flag for using the All Listings Page (ALP) as the front door / homepage
   * for unauthenticated or returning buyers (BP-2541).
   * When enabled, the root path redirects buyers to the ALP instead of the
   * traditional marketing homepage.
   */
  ALP_AS_FRONTPAGE: new FeatureFlag<boolean>('alp-as-frontpage', false),

  /**
   * The redesigned logged-out homepage. `ALP_AS_FRONTPAGE` still wins when both are on: the
   * experiment is sequenced by product, not by this flag.
   */
  ENABLE_LOGGED_OUT_HOMEPAGE_REDESIGN: new FeatureFlag(
    'enable-logged-out-homepage-redesign',
    false
  ),

  /**
   * Single gate for all "surface credit-card eligibility" experiences (BP-1832).
   * Replaces the separate `enable-ldp-cc-messaging` and `enable-alp-cc-tag`
   * flags so every CC-eligibility surface ships under one key:
   * - LDP "Credit Card Accepted" tag in `ListingTags`.
   * - ALP listing-card "Credit Card Accepted" tag in `BaseListingCard2025`.
   * - The `cc_eligible_tag_shown` property on the Segment
   *   `listing_card_impression` event in `ListingSearchProvider`, and the
   *   `cc_tag_viewed` / `cc_eligibility_variant` properties on the LDP
   *   `screen_impression` event in `useTrackPageViewed`.
   * Each surface still additionally requires `listing.isCreditCardEnabled`.
   */
  ENABLE_CC_SURFACING: new FeatureFlag('enable-cc-surfacing', false),

  // Gates the whole Seller Collects buyer experience (GLOB-4759). Off must
  // restore today's behavior exactly on every surface.
  SELLER_COLLECTS_BUYER_EXPERIENCE: new FeatureFlag(
    'seller-collects-buyer-experience',
    false
  ),

  ENABLE_GET_ENTERPRISE_ORDERS: new FeatureFlag(
    'enable-get-enterprise-orders',
    false
  ),

  /**
   * Feature flag for the Crosslister entry points (BP-2240).
   * Gates both buyer-portal entry points into the Crosslister sidecar app:
   * the "Resell" button on each Order card and the "Inventory" link in the
   * buyer account dropdown menu. Targeted in LaunchDarkly to a beta segment
   * of specific buyer accounts.
   */
  ENABLE_CROSSLISTER: new FeatureFlag('enable-crosslister', false),

  /**
   * Feature flag for geofenced Local Flat Rate shipping (GLOB-4113 / BP-1925).
   * Same LaunchDarkly key the shipment service reads. Passed as
   * `isGeofenceEnabled` to fe-core's `DeliveryMethodModalProvider` /
   * `SelectShippingModal` — off (or no `listing.geofence`) means all
   * locations resolve `geofencing: 'unrestricted'` and a geofenced listing
   * shows no flat rate at all (kill-switch behavior).
   */
  GEOFENCED_LISTINGS: new FeatureFlag('enable-geofenced-listings', false),

  /**
   * When enabled, shows a carousel of the buyer's recently viewed listings on the LDP
   * (below "More from this Seller") and on the homepage.
   * Auth-gated — only rendered for logged-in users.
   */
  ENABLE_RECENTLY_VIEWED_CAROUSEL: new FeatureFlag(
    'enable-ldp-recently-viewed-carousel',
    false
  ),

  /**
   * Enables the "Recommended for You" carousel on the Listing Details Page (LDP).
   * Control: { enabled: false } — no carousel shown (existing behavior).
   * Treatment: { enabled: true, excludedStorefrontIds: ['<id>'] } — carousel shown;
   * Costco storefront ID(s) supplied via LaunchDarkly.
   */
  ENABLE_LDP_RECOMMENDED_FOR_YOU: new FeatureFlag<{
    enabled: boolean
    excludedStorefrontIds?: string[]
  }>('enable-ldp-recommended-for-you', { enabled: false }),

  /**
   * Gates the "Recent Views" homepage similarity carousel — "Because you
   * viewed [listing]" candidates seeded from the buyer's view history.
   * Control: { enabled: false } — no carousel shown (existing behavior).
   * Treatment: { enabled: true } plus three independent, optional
   * storefront-keyed configs:
   * - excludedStorefrontIds — candidates from these storefronts never
   *   appear, same convention as ENABLE_LDP_RECOMMENDED_FOR_YOU.
   * - containedStorefrontIds — Costco containment: a seed whose own
   *   storefront is in this list only ever recommends candidates from
   *   that same storefront, no backfill. Move a storefront ID here (out of
   *   excludedStorefrontIds) once its rail is ready to run contained rather
   *   than hidden entirely.
   * - storefrontTuning — per-storefront overrides for the diversity cap
   *   (`candidatesPerSeed`) and similarity band (`priceBandTolerance`,
   *   `unitsBandTolerance`). A single-seller contained pool is inherently
   *   less diverse than the open rail, so it may need a tighter cap/band
   *   than the global default — unset means "inherit the global value."
   *   Independent of containedStorefrontIds: a storefront can have tuning
   *   without being contained, or be contained without an override.
   */
  ENABLE_RECENT_VIEWS_CAROUSEL: new FeatureFlag<{
    enabled: boolean
    excludedStorefrontIds?: string[]
    containedStorefrontIds?: string[]
    storefrontTuning?: Record<
      string,
      {
        candidatesPerSeed?: number
        priceBandTolerance?: number
        unitsBandTolerance?: number
      }
    >
  }>('enable-based-on-your-recent-views-carousel', { enabled: false }),

  /**
   * A/B experiment: buyer-level split among buyers with view history —
   * control sees no Recent Views carousel, treatment sees it. Gates the
   * pipeline fetch itself, not just rendering: a control-arm buyer never
   * pays the candidate pipeline's fetch/cache cost. Separate from
   * ENABLE_RECENT_VIEWS_CAROUSEL, which is the environment-level on/off
   * switch — both must be satisfied (enabled AND treatment) to render.
   */
  RECENT_VIEWS_CAROUSEL_AB: new FeatureFlag<'control' | 'treatment'>(
    'recent-views-carousel-ab',
    'control'
  ),

  /**
   * A/B experiment nested inside the Recent Views carousel's treatment arm:
   * true shows the "New" badge beside the carousel title, false doesn't.
   * Evaluated only at the end of the carousel's server gate, and compared
   * with `=== true`, so a missing or mistyped variation means no badge.
   * Owner: Team Buyer Pod. Remove after the readout.
   */
  NEW_FEATURE_BADGE_AB: new FeatureFlag('new-feature-badge', false),

  /**
   * Beta badge beside the Compare toggle; arm C of Compare's A/B/C test.
   * Read only by FeatureBadgeSlot, which renders where Compare is visible.
   * Owner: Team Buyer Pod. Remove with the badge once the test reads out.
   */
  BETA_BADGE_COMPARE_LISTINGS: new FeatureFlag(
    'beta-badge-compare-listings',
    false
  ),

  /**
   * Feature flag for the "Eligible Only" filter on the All Listings Page (GLOB-3879).
   * When enabled, shows a toggle to filter listings to only those the buyer is
   * eligible to purchase based on their DEFAULT_DELIVERABLE location.
   */
  ENABLE_ELIGIBLE_ONLY_FILTER: new FeatureFlag(
    'enable-buyer-eligibility-filter',
    false
  ),

  /**
   * A/B experiment: personalization sort vs. category-affinity sort for the
   * "Recommended for You" carousel (GLOB-4595 / BP-2531).
   * Control: sortBy=recommended + buyingMerchandise category filter.
   * Treatment: sortBy=personalization + buyingMerchandise category filter.
   * TODO: Remove once the A/B test is complete.
   */
  PERSONALIZED_CAROUSEL_AB: new FeatureFlag<'control' | 'treatment'>(
    'personalized-carousel-ab',
    'control'
  ),

  /**
   * A/B experiment: personalized default sort vs. current recommended default
   * on the ALP and browse pages (GLOB-4591).
   * Control: sortBy=recommended (current behavior).
   * Treatment: sortBy=personalization as the default for eligible buyers.
   * TODO: Remove once the A/B test is complete.
   */
  PERSONALIZED_SORT_AB: new FeatureFlag<'control' | 'treatment'>(
    'personalized-sort-ab',
    'control'
  ),

  /**
   * Master gate for the personalization experience. Consumed by fe-core's
   * usePersonalizationEligibility hook (client-side) for both the homepage
   * carousel and the ALP personalized sort (GLOB-4770).
   */
  ENABLE_PERSONALIZED_EXPERIENCE: new FeatureFlag(
    'enable-personalized-experience',
    false
  ),

  /**
   * List of Cloudflare country codes (e.g. ['US', 'CA']) for which the
   * personalization experience is allowed. Checked client-side via
   * fe-core's useIsCountryAllowed hook. Empty list = disabled for all countries.
   */
  PERSONALIZATION_ALLOWED_COUNTRIES: new FeatureFlag<string[]>(
    'personalization-allowed-countries',
    []
  ),

  /**
   * Gates the ranking-rationale subtitle on the Recommended for You carousel.
   * Off by default — the copy shown while this is enabled is not yet
   * Product-approved. This is a master kill switch, independent of the A/B
   * arm below — both must resolve favorably for the label to render.
   */
  RANKING_RATIONALE_LABEL: new FeatureFlag('ranking-rationale-label', false),

  /**
   * A/B experiment: ranking-rationale label vs. no label, layered on top of
   * the master RANKING_RATIONALE_LABEL gate above. Reuses the same
   * control/treatment shape as the existing personalized-carousel-ab
   * experiment, as an independent dimension on the same carousel.
   */
  RANKING_RATIONALE_LABEL_AB: new FeatureFlag<'control' | 'treatment'>(
    'ranking-rationale-label-ab',
    'control'
  ),

  /**
   * Feature flag for the Watchlist heart on ALP and Seller Storefront listing
   * cards (GLOB-4627 / BP-2317). When enabled, `ListingGridWithFilters` enriches
   * 3MP listings with `watchlistStatus`, which surfaces the heart on both
   * surfaces.
   * TODO: Remove once the A/B test is complete.
   */
  ENABLE_WATCHLIST_HEART_ON_CARDS: new FeatureFlag(
    'enable-watchlist-heart-on-cards',
    false
  ),

  /**
   * Watchlist → My Bids BidCard template A/B experiment.
   * `enabled` gates the template switch; `bulkBid` independently controls the
   * bulk-bid selection UI on the watchlist page.
   *
   * Requires ENABLE_MY_BIDS_INLINE_BID_BOX to be on — the watchlist-specific
   * card UI (Remove from Watchlist, notes, bid labels) lives inside BidCard's
   * inlineBidBoxEnabled branch and will not render if that flag is off.
   */
  ENABLE_WATCHLIST_MY_BIDS_TEMPLATE: new FeatureFlag<{
    enabled: boolean
    bulkBid: boolean
  }>('enable-watchlist-my-bids-template', {
    enabled: false,
    bulkBid: false,
  }),

  /**
   * Single gate for the Auction Schedule "TV guide" page (GLOB-4495 / BP-2348).
   * Controls the nav link (both header codepaths), the `/buy/auction-schedule`
   * route + auth gating, and the schedule grid — one flag, one A/B population
   * (all logged-in buyers).
   * TODO: Remove once the A/B test is complete and the winner is permanent.
   */
  ENABLE_TV_GUIDE_SCHEDULE: new FeatureFlag('enable-tv-guide-schedule', false),

  /**
   * TODO: Remove once the A/B test is complete and the winner is permanent.
   */
  ENABLE_SAVED_SEARCHES_PAGE: new FeatureFlag(
    'enable-saved-searches-page',
    false
  ),

  /**
   * Experiment arm for the saved-search highlights carousel.
   * 'off': carousel does not render.
   * 'standard': carousel renders without the few-bids filter.
   * 'few-bids': carousel renders with the server-side few-bids filter applied.
   * TODO: Remove once the experiment is complete and the winner is permanent.
   */
  EXPERIMENT_SAVED_SEARCH_HIGHLIGHTS_CAROUSEL: new FeatureFlag<
    'off' | 'standard' | 'few-bids'
  >('experiment-saved-search-highlights-carousel', 'off'),

  /**
   * Feature flag gating the "leave without saving?" exit warning on the
   * add-address wizard (new addresses only — matches AP).
   */
  ENABLE_EARLY_EXIT_ADD_ADDRESS_WARNING: new FeatureFlag(
    'enable-early-exit-add-address-warning',
    false
  ),

  /**
   * When enabled, shows a "Closing Soon Alerts" quick-toggle banner at the top of
   * the Watchlist page, letting buyers enable or disable email/SMS closing alerts
   * without navigating to the full Notification Preferences center.
   * Gated to the test group only — the control group must not see the banner.
   * TODO: Remove once the A/B test is complete.
   */
  WATCHLIST_CLOSING_ALERT: new FeatureFlag(
    'watchlist-closing-alert-enabled',
    false
  ),

  /**
   * Feature flag for the "Freeze Sort" control on ALP/Storefront List View
   * (GLOB-4832). Gates the toggle everywhere it's rendered (desktop List View
   * toolbar and the mobile filters modal) — the underlying freeze/unfreeze
   * logic in ListingSearchProvider is otherwise inert, since isSortFrozen can
   * only ever be set true via this toggle.
   * Control (A): flag off — no Freeze Sort control visible anywhere.
   * Test (B): flag on — Freeze Sort available.
   * TODO: Remove once the A/B test is complete.
   */
  ENABLE_FREEZE_SORT: new FeatureFlag('enable-freeze-sort', false),
  /** Points account-settings links at home-portal instead of accounts-portal. */
  MY_ACCOUNT_HP_NAV: new FeatureFlag('my-account-hp-nav', false),

  /**
   * Multi-User Phase 1B (GLOB-5191): the owner-only "User Management" tile on
   * the My Account hub and the `/buy/my-account/[accountId]/users` page it
   * links to. One flag for both surfaces so the read-only list (FP-3001) can
   * ship dark ahead of the invite/role/remove mutations (FP-3002).
   * Off -> tile hidden and the route redirects back to the hub.
   *
   * Live in LaunchDarkly (project `default`) since 2026-09-19: ON in
   * `development` only, off in qa/staging/test/production.
   */
  ENABLE_USER_MANAGEMENT: new FeatureFlag('enable-user-management', false),

  /**
   * Kill switch for forwarding the GA4 client/session ids (`gaClientId` /
   * `gaSessionId`) on bid and offer submission payloads (GLOB-4954), which the
   * backends use to stitch server-side conversions back to the GA session
   * (offline event capture).
   *
   * Off (default): `useGaSessionIdPayload()` contributes nothing and every
   * payload is byte-identical to before GLOB-4954.
   * On: the ids are forwarded whenever the GA cookies are present.
   *
   * The receiving services reject unknown body fields, so only enable this in
   * an environment where BOTH backend capture stories are already deployed —
   * mg-GLOB-4949-capture-ga-ids (auction) and mg-GLOB-4950-capture-ga-ids
   * (offering). Turn it off FIRST if either service is rolled back.
   */
  ENABLE_GA_OFFLINE_EVENT_CAPTURE: new FeatureFlag(
    'enable-ga-offline-event-capture',
    false
  ),

  /**
   * Gates the buyer's ability to change their own email on the personal
   * settings page. Fail-closed: the email edit control is only shown when this
   * resolves to boolean true. Mirrors the same gate enforced by the account
   * service backend.
   */
  ENABLE_BUYER_CAN_CHANGE_EMAIL: new FeatureFlag(
    'enable-buyer-can-change-email',
    false
  ),

  /**
   * Feature flag for the Bid Sheet Upload Tool (GLOB-4879): download a List
   * View export, fill in bid amounts offline, upload it back, and have the
   * system place those bids. Gates every piece as it ships — the Listing
   * ID/Minimum Required Bid/Bid Submission export columns (BP-2572), the
   * "Upload Bid Sheet" button (BP-2573), bulk placement (BP-2574), and the
   * After-Upload Report (BP-2575) — so the tool ships dark end-to-end until
   * ready, rather than exposing partial pieces mid-build.
   * TODO: Remove once the A/B test is complete.
   */
  ENABLE_BID_SHEET_UPLOAD: new FeatureFlag('enable-bid-sheet-upload', false),

  /**
   * Gates the Second-Chance Offer buyer review/accept pages
   * (`/buy/sco/[orderId]` and `/buy/sco/[orderId]/accepted`). Defense in
   * depth: the `second-chance` service's own orchestrator gate already
   * prevents the offer email from being sent when this is off, so this only
   * catches stale or forwarded links reaching the route directly. Off ->
   * route renders the SCO-unavailable placeholder instead of Review Offer.
   */
  ENABLE_SCO: new FeatureFlag('sco-enabled', false),

  /**
   * Kill switch for the BP-2618 listing-view event tracking (LDP load, AI
   * Summary popup open). Off -> `trackListingView` no-ops and the batched
   * flush never fires, so nothing queues or reaches the search/personalization
   * write endpoints.
   */
  ENABLE_LISTING_VIEW_TRACKING: new FeatureFlag(
    'enable-listing-view-tracking',
    false
  ),

  /**
   * Phase 1A secure-authentication rollout flag (GLOB-5050), owned by
   * accounts-portal, which reads the same key to route users without a method
   * through FusionAuth's self-service setup after login.
   *
   * Boolean rather than a mode: enforcement is FusionAuth's own MFA login
   * policy, so there is no "required" arm for the portals to read. Home-portal
   * only reads it to decide whether to show the Login & Security entry point
   * into FusionAuth's self-service page, which is inert before rollout.
   */
  ENABLE_MFA_SUGGESTION: new FeatureFlag<boolean>(
    'enable-mfa-suggestion',
    false
  ),

  /**
   * Buyer-level gate for the Notification Center bell/badge/tray in
   * GlobalHeader. Off -> bell icon is omitted entirely (today's behavior).
   * On -> bell shows the server-side unread count from
   * `GET v1/notification-read-receipts/count` and opens the notifications
   * dropdown.
   */
  ENABLE_NOTIFICATION_CENTER: new FeatureFlag(
    'enable-notification-center',
    false
  ),

  /**
   * Feature flag for the category composition threshold filter.
   * When enabled, shows a slider (5%/10%/25%/50%/100%) that filters listings to
   * those where the selected category makes up at least N% of the lot.
   *
   * The 10% default is derived, not stored: `categoryThreshold` stays null in
   * reducer state (and out of the URL) until the buyer picks a value, and
   * ListingSearchProvider's `effectiveState` substitutes DEFAULT_THRESHOLD
   * whenever this flag is ON and a category is selected. Don't dispatch the
   * default instead — UPDATE_FILTERS resets the page and saved-search state.
   * When OFF, `effectiveState` strips any threshold (e.g. from a shared URL).
   *
   * TODO: Remove once A/B test is complete.
   */
  ENABLE_CATEGORY_COMPOSITION_THRESHOLD: new FeatureFlag<boolean>(
    'enable-category-composition-threshold',
    false
  ),

  /**
   * Remembers the buyer's last items-per-page selection on the ALP and
   * storefronts (localStorage, this browser only). Large page sizes carry a
   * search-load cost, so this is targeted at a bounded key-buyer segment by
   * account id rather than rolled out to everyone; widening it is a separate
   * decision made against the cohort's load data.
   * Off -> today's behavior: nothing is read or written, and a URL without
   * `l` gets the view's default page size.
   */
  ENABLE_REMEMBER_PAGE_SIZE: new FeatureFlag<boolean>(
    'enable-remember-page-size',
    false
  ),

  /**
   * AT&T storefront access for Key Buyers. On -> a signed-in buyer on an AT&T
   * storefront gets account's storefront access decision before the terms
   * step: no access shows the gate, and a Key Buyer sees the Key Buyer Access
   * badge. Off -> the decision is never called (today's behavior).
   */
  ATT_KEY_BUYER_ACCESS: new FeatureFlag<boolean>('att-key-buyer-access', false),
} as const satisfies Partial<Record<string, FeatureFlag<unknown>>>
